import {debounce, difference, uniq, without} from 'lodash';
import memoize from 'memoize-one';
import {merge, NEVER, Observable, Subject, Subscription} from 'rxjs';
import {filter, map, pairwise, skipUntil, startWith} from 'rxjs/operators';

import {inAnimationFrameOrHiddenCallbackAsync} from '../../libs/shared/core/src/helpers/async/inAnimationFrame';
import {globalWindow} from '../../libs/shared/core/src/helpers/browser/browserHelpers';
import {isOnline} from '../../libs/shared/core/src/helpers/browser/networkReachabilityHelpers';
import {isDevelopment, isProduction} from '../../libs/shared/core/src/helpers/environment/envHelpers';
import {
  annotateTaskStep,
  TaskSourcesEnum,
} from '../../libs/shared/core/src/helpers/instrumentation/eventLoopPerformanceHelpers';
import {ExtendedError, UnreachableError} from '../../libs/shared/core/src/helpers/types/customErrors';
import {isDefined} from '../../libs/shared/core/src/helpers/typescript/typeHelpers';
import {
  buildCancelTokenSource,
  CancelToken,
  CancelTokenSource,
} from '../../libs/shared/core/src/helpers/utilities/asyncCancelHelpers';
import {CompanyModel} from '../../libs/shared/core/src/models/api/company/companyModel';
import {PushConfigModel} from '../../libs/shared/core/src/models/api/pushConfigModel';
import {TeammateModel} from '../../libs/shared/core/src/models/api/teammateModel';
import {leaveBreadcrumb, reportToBugsnag} from '../../libs/shared/core/src/services/bugsnag/bugsnag';
import {info, trace} from '../../libs/shared/core/src/services/debug/debug';
import {httpLogger} from '../../libs/shared/core/src/services/http/http';
import {logger} from '../../libs/shared/core/src/services/logger/logger';
import {
  LogPushEventHandledParams,
  PushProvidersEnum,
  PushSourcesEnum,
  pushV1Logger,
  PushVersionsEnum,
} from '../../libs/shared/core/src/services/push/common/pushLogger';
import {PushMonitor} from '../../libs/shared/core/src/services/push/common/pushMonitor';
import {PushTask, PushTaskQueue} from '../../libs/shared/core/src/services/push/common/pushTaskQueue';
import {PushEventSubscriber, PushProps} from '../../libs/shared/core/src/services/push/pushContext';
import {
  PushCallback,
  PushEventDefinition,
  PushRenderTask,
} from '../../libs/shared/core/src/services/push/pushTypes';
import {reportPushConnectivity} from '../../src/services/heartbeat';
import {makePushSocket} from '../../src/services/push';
import {
  addReconnectMetadata,
  AppTransitionStepsEnum,
  findCurrentAppTransitionTrackingId,
  markAppStateTransitionStep,
} from '../services/performanceMetrics/appStateTransitionPerformance';
import {AblySocket, ConnectionState} from '../services/push/ably';
import {PushSocket} from '../services/push/pushSocket';
import {
  fetchUpdatedConversation,
  FetchUpdatedConversationOptions,
  makeFetchUpdatedConversationTasksSubject,
  PushLifecycleEventsEnum,
  UpdatedConversationRenderCallback,
} from './pushCommons';
import {getConversationRoomName} from './pushHelpers';
import {PresenceSubscriptions} from './pushPresenceSubscriptions';

interface PushConnectPayload {
  rooms: ReadonlyArray<string>;
  events: ReadonlyArray<PushEventDefinition>;
  relayedEvents?: ReadonlyArray<string>;
}

/*
 * Types.
 */

export interface PushCoordinatorConfiguration {
  pushConfiguration: PushConfigModel;
  company: CompanyModel;
  user: TeammateModel;
}

interface RefreshContentData {
  reconnectDelay: number;
  timeSinceReconnect: number;
  initialBacklogLength: number;
  currentBacklogLength: number;
}

type PushReceivedCallback = (data: any) => void;
type PushSubscribeOptions = {
  /** Lightweight callback that is called when the relevant event type is received.
   *  Warning: This should only be used for very fast operations such as logging the receive time.
   */
  onPushReceived?: PushReceivedCallback;
};

type Unsubscribe = () => void;

/*
 * Manager.
 */

const pushLogger = logger.getLogger('push');

const shouldCreatePerformanceEntries = 'mark' in performance && isDevelopment();

export class PushCoordinator {
  constructor(
    configuration: PushCoordinatorConfiguration,
    private readonly onConversationUpdatedAction: UpdatedConversationRenderCallback,
    private readonly onRefreshContent: (cancelToken: CancelToken) => void,
  ) {
    const {pushConfiguration, company, user} = configuration;
    this.pushMonitor = new PushMonitor(PushVersionsEnum.V1, PushProvidersEnum.ABLY, PushSourcesEnum.APP);

    this.socket = makePushSocket(
      pushConfiguration,
      () => company,
      () => this.isNetworkOnline,
      user,
      this.pushMonitor,
    );
    this.subscribers = [];
    this.roomSubscribes$ = new Subject();

    const reconnectOnBacklogOptions = {
      getLastReconnectTimestamp: () => this.lastReconnect,
      refreshContent: this.refreshContentOnBacklog.bind(this),
    };
    const featureFlagOptions = {
      reconnectOnBacklogOptions,
    };
    this.pushTaskQueue = new PushTaskQueue(PushVersionsEnum.V1, featureFlagOptions);

    this.isNetworkOnline = isOnline();
    this.wasDisconnected = !isOnline();
    this.isPushDisconnectedSubject$ = new Subject();
    this.rxSubscription = new Subscription();
  }

  private readonly isPushDisconnectedSubject$: Subject<boolean>;
  private rxSubscription: Subscription;

  private readonly socket: PushSocket;
  private lastPushDisconnect: number | null = null;
  private lastReconnect: number | null = null;
  private wasDisconnected: boolean;
  // TODO: Only used by push socket for temporary logging. To be removed.
  private isNetworkOnline: boolean;
  private lastNetworkDisconnect: number | null = null;
  private roomSubscribes$: Subject<string>;
  private subscribers: ReadonlyArray<PushEventSubscriber>;
  private connectionPayload: PushConnectPayload | undefined;
  private readonly pushTaskQueue: PushTaskQueue;
  private readonly pushMonitor: PushMonitor;

  private fetchBootCancelTokenSource?: CancelTokenSource;

  readonly fetchUpdatedConversationTasks$ = makeFetchUpdatedConversationTasksSubject(
    this.fetchUpdatedConversation.bind(this),
  );

  enqueueTask(task: PushTask) {
    this.pushTaskQueue.enqueue(task);
  }

  private cancelQueuedTasks() {
    this.pushTaskQueue.cancelAll();
  }

  enqueueDelayedRenderTask(renderTask: PushRenderTask, delay: number) {
    globalWindow.setInstrumentedTimeout(() => this.enqueueTask(() => () => renderTask()), delay, {
      source: TaskSourcesEnum.PUSHER,
    });
  }

  /*
   * Lifecycle events.
   */

  /**
   * Configure a PushCoordinator instance to react to connection status changes.
   *  **Every call to this method must be balanced with a `destroy()` call.**
   * @param  {ReadonlyArray<string>} rooms - The initial list of rooms to subscribe to.
   * @param  {PresenceSubscriptions} presences - The list of presence channels to subscribe to.
   * @param  {ReadonlyArray<PushEventDefinition>} events - The list of event definitions to subscribe to.
   * @param  {ReadonlyArray<string>|undefined} relayedEvents - An optional list of events to relay through the legacy frame.
   * @param  {Observable<boolean>} networkStatusEvents$
   * - An observable emitting value each time the connectivity status changes.
   * The coordinator will try to reconnect every time `true` is emitted as long as `connectionControlChange$` allows it.
   * @param  {Observable<PushLifecycleEventsEnum>} pushLifecycleEvents$
   * - An observable controlling the desired state of the connection. The coordinator will try to reconnect every time `CONNECT` is emitted
   * and will ignore all reconnection events emitted after `DISCONNECT`. Default to `NEVER`.
   * @param {boolean} shouldRefreshBootUponFirstConnection - Make a call to refreshContent right after the first connection if true.
   * @see {@link destroy} for further information.
   */
  setup(
    rooms: ReadonlyArray<string>,
    presences: PresenceSubscriptions,
    events: ReadonlyArray<PushEventDefinition>,
    relayedEvents: ReadonlyArray<string> | undefined,
    networkStatusEvents$: Observable<boolean>,
    pushLifecycleEvents$: Observable<PushLifecycleEventsEnum> = NEVER,
    shouldRefreshBootUponFirstConnection: boolean = false,
  ) {
    // Setup reconnection.
    const networkLifecycleEvents$ = networkStatusEvents$.pipe(
      skipUntil(this.pushDisconnectEvents$),
      map((isNetworkOnline) => {
        if (isNetworkOnline) {
          return PushLifecycleEventsEnum.RECONNECT;
        }

        if (!isNetworkOnline && this.isConnected) {
          // Perform a health check if we're still connected.
          return PushLifecycleEventsEnum.PING;
        }

        return undefined;
      }),
      filter(isDefined),
    );

    const lifecycleEventsObservables = [networkLifecycleEvents$, pushLifecycleEvents$];
    this.pushMonitor.start();

    this.connectionPayload = {rooms, events, relayedEvents};

    this.rxSubscription.add(
      merge(...lifecycleEventsObservables)
        .pipe(
          startWith(PushLifecycleEventsEnum.CONNECT),
          pairwise(),
          filter(
            ([previous, current]) =>
              previous !== PushLifecycleEventsEnum.DISCONNECT || current === PushLifecycleEventsEnum.CONNECT,
          ),
          map(([_, current]) => current),
        )
        .subscribe(
          this.onConnectionControlChange,
          (error) => reportToBugsnag(new ExtendedError('Push connection control pipe broken', error)),
          () => reportToBugsnag(new Error('Push connection control pipe completed')),
        ),
    );

    this.rxSubscription.add(
      networkStatusEvents$.subscribe(
        this.onConnectivityChange,
        (error) => reportToBugsnag(new ExtendedError('Network monitor pipe broken', error)),
        () => reportToBugsnag(new Error('Network monitor pipe completed')),
      ),
    );

    // Pusher connection lifecycle callbacks.
    let appStateTransitionTracking: {trackingId: string; startTime: number} | undefined;
    this.socket.on(
      'connected',
      (
        options: {shouldResubscribeToRooms: boolean} = {
          shouldResubscribeToRooms: true,
        },
      ) => {
        info('Push connected.');
        trace('push-status', {status: 'connected'});

        if (appStateTransitionTracking) {
          const {trackingId, startTime} = appStateTransitionTracking;
          markAppStateTransitionStep(trackingId, AppTransitionStepsEnum.PUSH_RECONNECT, startTime);
          appStateTransitionTracking = undefined;
        }

        const socketId = this.socket.id;
        if (!socketId) {
          pushLogger.warn('Socket connected without an ID');
          return;
        }

        // TODO: if the underlying socket disconnects and then reconnects, the following
        // will cause the socket to subscribe to the initial list of rooms used at boot.
        // This should instead be the rooms for which the user is *currently* meant to be
        // subscribed to.
        if (options.shouldResubscribeToRooms) {
          this.updateRoomSubscriptions(rooms);
        }
        this.reportPushConnectivity(true);

        // Check if we need to refresh the app's content.
        if (!this.wasDisconnected) {
          return;
        }

        this.refreshContentDebounced();
      },
    );

    this.socket.on(
      'disconnected',
      (options: {shouldUnsubscribeFromRooms: boolean} = {shouldUnsubscribeFromRooms: true}) => {
        info('Push disconnected.');
        trace('push-status', {status: 'disconnected'});

        this.wasDisconnected = true;
        if (options.shouldUnsubscribeFromRooms) {
          this.unsubscribeFromRooms();
        }
        this.reportPushConnectivity(false);
      },
    );

    this.socket.on('connecting', (options: {previousState: ConnectionState}) => {
      const {previousState} = options;
      const isReconnecting = previousState !== 'connected' && previousState !== 'initialized';
      info('Push ', isReconnecting ? 'reconnecting' : 'connecting');

      // Start measuring reconnection time
      const trackingId = findCurrentAppTransitionTrackingId();
      if (trackingId) {
        appStateTransitionTracking = {trackingId, startTime: performance.now()};
      }
    });

    // Presence events.
    presences.forEach((presence) => {
      const {name, handler} = presence;
      this.subscribeToPresenceEvent(name, handler);
    });

    // Start socket connection.
    this.setupSocket({rooms, events, relayedEvents});

    if (shouldRefreshBootUponFirstConnection) {
      this.refreshContentDebounced();
    }
  }

  /**
   * Stop Pusher and unregister all listeners and subscriptions.
   * @see {@link setup} for how to configure coordinator.
   */
  destroy() {
    this.rxSubscription.unsubscribe();

    if (!this.socket) {
      return;
    }

    this.unsubscribeFromRooms();

    // Cleanup the socket.
    this.socket.removeAllListeners();
    this.disconnectSocket();
    this.pushMonitor.stop();
  }

  /*
   * Accessors.
   */

  get isConnected() {
    return this.socket.isConnected;
  }

  get pushDisconnectEvents$(): Observable<boolean> {
    return this.isPushDisconnectedSubject$;
  }

  /*
   * Push events.
   */

  private reportPushConnectivity(connected: boolean) {
    this.isPushDisconnectedSubject$.next(!connected);

    if (connected) {
      const duration = this.lastPushDisconnect ? Math.round(performance.now() - this.lastPushDisconnect) : 0;
      this.lastPushDisconnect = null;
      reportPushConnectivity(true, duration);
    } else {
      this.lastPushDisconnect = performance.now();
      reportPushConnectivity(false);
    }
  }

  private setupSocket({rooms, events, relayedEvents}: PushConnectPayload) {
    // Connect the driver.
    this.socket.connect();

    events.forEach((subscription) => {
      const {name: eventName, handler, onReceiveHandler: onPushReceived, isDirect} = subscription;
      const options = onPushReceived ? {onPushReceived} : undefined;

      if (isDirect) {
        this.subscribeToDirectPushEvent(eventName, handler, options);
      } else {
        this.subscribeToPushEvent(eventName, handler, options);
      }
    });

    relayedEvents?.forEach((eventName) => {
      this.relayPushEvent(eventName);
    });

    this.updateRoomSubscriptions(rooms);
  }

  private readonly connectSocket = () => {
    if (this.connectionPayload) {
      return this.setupSocket(this.connectionPayload);
    }
    throw new Error('Invalid State: Push Coordinator attempted a connection before its setup');
  };

  private disconnectSocket() {
    this.socket.disconnect();
    this.subscriptions = {};
    this.updateConnectionPayload([]);
  }

  private reconnectSocket() {
    this.socket.reconnect();
  }

  private ping() {
    this.socket.ping();
  }

  private readonly onConnectionControlChange = (control: PushLifecycleEventsEnum) => {
    switch (control) {
      case PushLifecycleEventsEnum.CONNECT:
        return this.connectSocket();
      case PushLifecycleEventsEnum.RECONNECT:
        return this.maybeReconnectSocket();
      case PushLifecycleEventsEnum.DISCONNECT:
        return this.disconnectSocket();
      case PushLifecycleEventsEnum.PING:
        return this.ping();
      default:
        throw new UnreachableError(control);
    }
  };

  /*
   * Room Subscriptions.
   */

  private unsubscribeFromRooms() {
    this.updateRoomSubscriptions([]);
  }

  private subscriptions: {[room: string]: Unsubscribe} = {};

  isSubscribed(roomName: string): boolean {
    return this.subscriptions[roomName] !== undefined;
  }

  private isSubscribedToConversationRoom(conversationId: number): boolean {
    return this.isSubscribed(getConversationRoomName(conversationId));
  }

  shouldDiscardServerPrecog(conversationId: number, isPrecogAlreadySentDirectly: boolean) {
    const isReceivingDirectPrecogs = this.isSubscribedToConversationRoom(conversationId);

    // We assume that we already received the direct precogs if they were sent by the other client and we're connected to the corresponding room.
    return isReceivingDirectPrecogs && isPrecogAlreadySentDirectly;
  }

  updateRoomSubscriptions(rooms: ReadonlyArray<string>) {
    this.updateConnectionPayload(rooms);

    // Get the previous and next set of rooms we want to be subscribed to.
    const previousRooms = Object.keys(this.subscriptions);
    const nextRooms = uniq(rooms);

    // Compute the difference.
    const subscribeToRooms = difference(nextRooms, previousRooms);
    const unsubscribeFromRooms = difference(previousRooms, nextRooms);

    subscribeToRooms.forEach((room) => this.pushMonitor.logChannelSubscribed(room));
    unsubscribeFromRooms.forEach((room) => this.pushMonitor.logChannelUnsubscribed(room));

    // Unsubscribe.
    unsubscribeFromRooms.forEach((room) => {
      try {
        // No need to unsubscribe if we're not connected.
        if (!this.socket.isConnected) {
          return;
        }

        const unsubscribe = this.subscriptions[room];
        if (!unsubscribe) {
          return;
        }

        // Try to unsubscribe.
        unsubscribe();
      } catch (error) {
        reportToBugsnag(new ExtendedError(`Failed to unsubscribe from room '${room}'`, error));
      } finally {
        // Remove from the subscriptions no matter what.
        delete this.subscriptions[room];

        // Maybe update the underlying driver subscriptions.
        if (this.socket.replaceSubscriptions) {
          this.socket.replaceSubscriptions([...Object.keys(this.subscriptions)]);
        }
      }
    });

    // Bail if we're not connected.
    if (!this.socket.isSocketOpen || !this.socket.isConnected) {
      if (this.socket.isConnected && !this.socket.isSocketOpen) {
        pushLogger.warn('Inconsistent socket state', {
          socketOpen: this.socket.isSocketOpen,
          socketConnected: this.socket.isConnected,
          wasDisconnected: this.wasDisconnected,
          isNetworkOnline: this.isNetworkOnline,
          listenerCount: this.socket.listenerCount('connected'),
        });
      }
      return;
    }

    // Subscribe.
    subscribeToRooms.forEach((room) => {
      // Maybe update the underlying driver subscriptions.
      if (this.socket.replaceSubscriptions) {
        this.socket.replaceSubscriptions([...Object.keys(this.subscriptions), room]);
      }

      const {unsubscribe} = this.socket.subscribe(room, this.roomSubscribes$);
      this.subscriptions[room] = unsubscribe;
    });
  }

  private updateConnectionPayload(rooms: ReadonlyArray<string>) {
    if (!this.connectionPayload) {
      return;
    }

    this.connectionPayload.rooms = rooms;
  }

  /*
   * Presence event subscriptions.
   */

  subscribeToPresenceEvent(eventName: string, listener: (param: any) => void) {
    this.socket.on(eventName, listener);
  }

  /*
   * Event Subscriptions.
   */

  private subscribeToPushEvent(
    eventName: string,
    callback: PushCallback,
    options: PushSubscribeOptions = {},
  ) {
    this.onPushEvent(eventName, callback, true, false, options);
  }

  private subscribeToDirectPushEvent(
    eventName: string,
    callback: PushCallback,
    options: PushSubscribeOptions = {},
  ) {
    this.onPushEvent(eventName, callback, true, true, options);
  }

  private relayPushEvent(eventName: string, options: PushSubscribeOptions = {}) {
    this.onPushEvent(eventName, undefined, false, false, options);
  }

  private onPushEvent(
    eventName: string,
    callback: PushCallback | undefined,
    shouldLogEvent: boolean,
    isDirectEvent: boolean = false,
    options: PushSubscribeOptions = {},
  ) {
    const onPush = (isDirectEvent ? this.socket.onDirectPushEvent : this.socket.onPushEvent).bind(
      this.socket,
    );

    onPush(eventName, (data) => {
      // Maybe log the event for easy debugging.
      if (shouldLogEvent) {
        logEvent(eventName, data);
      }

      if (isDirectEvent) {
        inAnimationFrameOrHiddenCallbackAsync(
          () => {
            if (callback) {
              callback(data)();
            }
          },
          {source: TaskSourcesEnum.PUSHER},
        );

        return;
      }

      const receiveTime = performance.now();

      // Lightweight callback that can be set (e.g. to log when the event was received).
      maybeInvokeOnPushReceivedCallback(eventName, options.onPushReceived, data);

      this.enqueueTask((didTimeout) => {
        const callbackStartTime = performance.now();
        const renderCallback = maybeInvokePushCallback(eventName, callback, data);
        const callbackEndTime = performance.now();
        this.subscribers.forEach((s) => s(eventName, data));

        return () => {
          const renderStartTime = performance.now();
          maybeInvokePushRenderCallback(eventName, renderCallback);
          const renderEndTime = performance.now();

          const params: LogPushEventHandledParams = {
            eventName,
            renderStartTime,
            renderEndTime,
            callbackStartTime,
            callbackEndTime,
            receiveTime,
          };

          if (didTimeout) {
            pushV1Logger.logPushEventHandledLate(params);
          } else {
            pushV1Logger.logPushEventHandled(params);
          }
        };
      });
    });
  }

  /*
   * Fetch updated conversations.
   */

  private fetchUpdatedConversation(options: FetchUpdatedConversationOptions) {
    const renderCallbackAsync = this.onConversationUpdatedAction;
    fetchUpdatedConversation(options, renderCallbackAsync, (task) => this.enqueueTask(task));
  }

  private enqueueFetchUpdatedConversation(options: FetchUpdatedConversationOptions) {
    this.fetchUpdatedConversationTasks$.next(options);
  }

  onFetchUpdatedConversation(options: FetchUpdatedConversationOptions) {
    this.enqueueFetchUpdatedConversation(options);
  }

  /*
   * Refresh content logic.
   */

  refreshContent() {
    // If needed, cancel the debounced call to refresh the content.
    this.refreshContentDebounced.cancel();

    // Save the time of this reconnect.
    this.lastReconnect = performance.now();

    // If needed, cancel the previous request.
    if (this.fetchBootCancelTokenSource) {
      this.fetchBootCancelTokenSource.cancel();
    }

    const cancelTokenSource = buildCancelTokenSource();
    const cancelToken = cancelTokenSource.token;
    this.fetchBootCancelTokenSource = cancelTokenSource;

    // Cancel old push events, since we may be recovering from an overload of push events, and we're about to
    // get a fresh boot anyway.
    this.cancelQueuedTasks();

    // Ask component to refresh the app content (boot, feature flags, sync cells list).
    this.onRefreshContent(cancelToken);
  }

  /** Debounced refresh content for online status changes. */
  private readonly refreshContentDebounced = debounce(this.refreshContent, 1000);

  /** Reset event queue to unfreeze app when a push event flood is detected */
  private refreshContentOnBacklog(data: RefreshContentData) {
    // Reconnect to socket.
    this.socket.reconnect();

    // Cancel queued tasks to unfreeze the app.
    this.cancelQueuedTasks();

    pushV1Logger.logPushEventFlood(
      data.initialBacklogLength,
      data.currentBacklogLength,
      data.reconnectDelay,
      data.timeSinceReconnect,
    );
  }

  /*
   * Network monitoring.
   */

  private maybeReconnectSocket() {
    const appStateTransitionId = findCurrentAppTransitionTrackingId();
    if (appStateTransitionId && this.socket instanceof AblySocket) {
      addReconnectMetadata(appStateTransitionId, this.isConnected, this.socket.socketState);
    }
    if (this.isConnected) {
      // Forcing socket to update its internal state
      this.socket.ping();

      // We don't want to reconnect pusher if we're already connected.
      return;
    }

    this.reconnectSocket();
  }

  private readonly onConnectivityChange = (isNetworkOnline: boolean): void => {
    this.isNetworkOnline = isNetworkOnline;
    if (isNetworkOnline) {
      httpLogger.info(`Network is online`, {
        is_push_connected: this.isConnected,
        is_navigator_online: isOnline(),
        reconnect_duration: this.lastNetworkDisconnect ? performance.now() - this.lastNetworkDisconnect : 0,
      });
      this.lastNetworkDisconnect = null;
    } else {
      httpLogger.info(`Network is offline`, {
        is_push_connected: this.isConnected,
        is_navigator_online: isOnline(),
      });
      this.lastNetworkDisconnect = performance.now();
    }
  };

  /*
   * Context.
   */

  /** Allow context consumers to register Push event listeners and act on them accordingly. */
  private readonly subscribeToEvents = (subscriber: PushEventSubscriber) => {
    this.subscribers = [...this.subscribers, subscriber];
    return () => {
      this.subscribers = without(this.subscribers, subscriber);
    };
  };

  // Ensures the function stays stable in the context and we have the right this
  private readonly emitDirectEvent = (roomName: string, eventName: string, data: any): void => {
    this.socket.emitDirectEvent(roomName, eventName, data);
  };

  readonly buildContextValue = memoize((connectionKey: number, isConnected: boolean): PushProps => {
    const {socket} = this;

    return {
      isConnected,
      connectionKey,
      subscribeToEvents: this.subscribeToEvents,
      emitDirectEvent: this.emitDirectEvent,
      roomSubscribes$: this.roomSubscribes$,
      getRoom: (roomName: string) => ({
        name: roomName,
        get isReceiving() {
          return socket.getIsReceiving(roomName);
        },
        isReceiving$: socket.getIsReceiving$(roomName),
      }),
    };
  });
}

/*
 * Helpers.
 */

function maybeInvokeOnPushReceivedCallback(
  eventName: string,
  callback: PushReceivedCallback | undefined,
  data: any,
) {
  if (!callback) {
    return;
  }

  try {
    callback(data);
  } catch (error) {
    // Wrap the error for better reporting.
    throw new ExtendedError(`Error in push onReceive callback for ${eventName}`, error);
  }
}

function maybeInvokePushCallback(
  eventName: string,
  callback: PushCallback | undefined,
  data: any,
): PushRenderTask | undefined {
  try {
    return callback && callback(data);
  } catch (error) {
    // Wrap the error for better reporting.
    throw new ExtendedError(`Error in push callback for ${eventName}`, error);
  }
}

function maybeInvokePushRenderCallback(eventName: string, callback: PushRenderTask | undefined) {
  if (!callback) {
    return;
  }

  annotateTaskStep(`push render callback ${eventName}`);
  const measureName = `Render ${eventName} event`;

  if (shouldCreatePerformanceEntries) {
    performance.mark(measureName);
  }

  try {
    callback();
  } catch (error) {
    // Wrap the error for better reporting.
    throw new ExtendedError(`Error in push render callback for ${eventName}`, error);
  } finally {
    if (shouldCreatePerformanceEntries) {
      performance.measure(measureName, measureName);
    }
  }
}

// TEMPORARY: For debug purposes.
const isVerbose = isProduction();
// Change this while developing if you want the logs back.
const shouldConsoleLog = false;
function logEvent(eventName: string, data: any) {
  if (!isVerbose) {
    return;
  }

  if (shouldConsoleLog) {
    info('Got event:', eventName, data);
  } else {
    leaveBreadcrumb(
      `[PushCoordinator] Got event: ${eventName}`,
      {
        eventName,
        blob: JSON.stringify(summarizePushContainerForBreadcrumbs(data), null, 2),
      },
      'Pusher event',
    );
  }
  trace(eventName, data);
}

/**
 * Filter out potentially sensitive keys from push container data.
 */
function summarizePushContainerForBreadcrumbs(data: any) {
  if (!data || typeof data !== 'object') {
    return data;
  }

  const filtered: any = {};
  const allowedKeysRe = /(?:^|_)(?:url|u?id|at|^[a-zA-Z]*[a-z](?:At|Id)|^class|^type)$/;
  Object.entries(data).forEach(([key, value]) => {
    if (key.match(allowedKeysRe) && ['string', 'number'].includes(typeof value)) {
      filtered[key] = value;
    }
  });

  return filtered;
}

export function makeEventSubscriptionsDelegate(coordinator: PushCoordinator) {
  return {
    enqueueDelayedRenderTask: coordinator.enqueueDelayedRenderTask.bind(coordinator),
    shouldDiscardServerPrecog: coordinator.shouldDiscardServerPrecog.bind(coordinator),
    onFetchUpdatedConversation: coordinator.onFetchUpdatedConversation.bind(coordinator),
    refreshContent: coordinator.refreshContent.bind(coordinator),
  };
}
